/**
 * Page-level scan capture for the Rollo scan station kiosk. A keyboard-wedge QR scanner (the MJ-2020R)
 * "types" the code as a fast keystroke burst terminated by Enter. Instead of a captive text input the
 * operator has to keep focused (fragile on an iPad — a stray tap steals focus, and iOS can raise the
 * on-screen keyboard), we listen at the document level so the WHOLE screen is the scan target.
 *
 * The buffering decision is a pure reducer (`feedScanKey`) so it is unit-tested without a DOM; the hook
 * (`useScannerCapture`) is a thin `document` keydown wrapper around it.
 */
import { useEffect, useRef } from 'react';

/**
 * A gap between keystrokes larger than this means a NEW sequence started — drop any stale partial buffer
 * (e.g. an interrupted scan that never sent Enter). A wedge scanner emits a whole code in well under
 * this; the value only has to sit above scanner jitter and below human "start a fresh scan" cadence.
 */
export const SCAN_INTERKEY_RESET_MS = 500;

export interface ScanBuffer {
  buffer: string;
  lastTs: number;
}

export function emptyScanBuffer(): ScanBuffer {
  return { buffer: '', lastTs: 0 };
}

export interface ScanFeedResult {
  state: ScanBuffer;
  /** A completed scan code — Enter pressed on a non-empty buffer — else undefined. */
  scan?: string;
}

/**
 * Fold one keydown into the buffer. `key` is `KeyboardEvent.key`; `timeStamp` a monotonic millisecond
 * clock (`KeyboardEvent.timeStamp` in the browser, explicit in tests). Printable single characters
 * accumulate (a gap over `resetMs` restarts the buffer); Enter flushes a trimmed non-empty buffer as a
 * scan and clears it; every other key (Shift, Tab, arrows, F-keys, …) is ignored.
 */
export function feedScanKey(
  state: ScanBuffer,
  key: string,
  timeStamp: number,
  resetMs: number = SCAN_INTERKEY_RESET_MS,
): ScanFeedResult {
  if (key === 'Enter') {
    const scan = state.buffer.trim();
    return { state: { buffer: '', lastTs: timeStamp }, scan: scan.length > 0 ? scan : undefined };
  }
  if (key.length === 1) {
    const stale = timeStamp - state.lastTs > resetMs;
    const buffer = (stale ? '' : state.buffer) + key;
    return { state: { buffer, lastTs: timeStamp } };
  }
  return { state };
}

/** Is the event target an editable element we must NOT hijack (the manual-entry field, etc.)? */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export interface UseScannerCaptureOptions {
  onScan: (code: string) => void;
  /** When false the listener is detached — e.g. while the manual-entry field is open. */
  enabled?: boolean;
}

/**
 * Attach a document-level keydown listener that emits a scan code when the wedge scanner finishes a
 * burst (Enter). Ignores keystrokes while an editable element is focused (so manual entry isn't
 * double-read) and ignores modified keys (Ctrl/Meta/Alt shortcuts). No visible input, nothing to keep
 * focused. `onScan` may change between renders without re-attaching (held in a ref).
 */
export function useScannerCapture({ onScan, enabled = true }: UseScannerCaptureOptions): void {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const bufRef = useRef<ScanBuffer>(emptyScanBuffer());

  useEffect(() => {
    if (!enabled) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      // A scan must never ALSO activate a FOCUSED control: a press / "Confirm reprint" / undo button
      // that kept focus from a prior tap would otherwise be fired by the scan's own key events —
      // Enter AND Space both synthesize a click on a focused native <button> — minting/pressing with no
      // deliberate tap. Suppress the default for both so a scan can't double as a click. (Space is still
      // buffered as a normal payload char below; preventDefault only cancels the activation/scroll.)
      if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
      const { state, scan } = feedScanKey(bufRef.current, e.key, e.timeStamp);
      bufRef.current = state;
      if (scan !== undefined) onScanRef.current(scan);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}
